import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import type { ObjectGraphConfigurationLocalStorage } from '../types.tsx';

/**
 *
 * The legacy insight UI currently relies on localStorage to persist a lot of shared state
 * This module exists to be able to read that data to populate areas of the UI we have re-written in React.
 * We want to migrate away from the reliance on localStorage and over time we can delete the functions in this file.
 *
 */
const OBJECT_GRAPH_CONFIGURATION_KEY = 'rlabs.rlabs-graph-configuration-object';
export const getGraphConfigurationLocalStorage = ():
	| ObjectGraphConfigurationLocalStorage
	| undefined => {
	try {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		if (window.localStorage) {
			// this will be available if users configure the object graph via the legacy UI

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			const graphConfig = window.localStorage.getItem(OBJECT_GRAPH_CONFIGURATION_KEY);
			if (graphConfig) {
				const { referenceDepth, maxOfSameType } = JSON.parse(graphConfig);
				return { referenceDepth, maxOfSameType };
			}
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		log.safeErrorWithoutCustomerData(
			'rlabs.localStorage.error',
			`Unable to get rlabs graph configuration in localStorage with error: ${error.toString()}`,
		);
	}
	return undefined;
};

export const setGraphConfigurationLocalStorage = (
	updatedConfig: ObjectGraphConfigurationLocalStorage,
): void => {
	try {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		if (window.localStorage) {
			const existingConfig = getGraphConfigurationLocalStorage();

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.localStorage.setItem(
				OBJECT_GRAPH_CONFIGURATION_KEY,
				JSON.stringify({ ...existingConfig, ...updatedConfig }),
			);
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		log.safeErrorWithoutCustomerData(
			'rlabs.localStorage.error',
			`Unable to set rlabs graph configuration in localStorage with error: ${error.toString()}`,
		);
	}
};

export const getReferenceCheckedLocalStorage = ({
	objectTypeId,
	referenceLabel,
}: {
	objectTypeId: string;
	referenceLabel: string;
}): boolean | undefined => {
	try {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		if (window.localStorage) {
			const key = `rlabs.rlabs-graph-objectType${objectTypeId}-reference${referenceLabel}`;

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			const isChecked = window.localStorage.getItem(key);
			// @ts-expect-error - TS2345 - Argument of type 'string | null' is not assignable to parameter of type 'string'.
			return JSON.parse(isChecked);
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		log.safeErrorWithoutCustomerData(
			'rlabs.localStorage.error',
			`Unable to get rlabs graph configuration in localStorage with error: ${error.toString()}`,
		);
	}
	return undefined;
};

export const setReferenceCheckedLocalStorage = ({
	objectTypeId,
	referenceLabel,
	updatedValue,
}: {
	objectTypeId: string;
	referenceLabel: string;
	updatedValue: boolean;
}): void => {
	try {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		if (window.localStorage) {
			const key = `rlabs.rlabs-graph-objectType${objectTypeId}-reference${referenceLabel}`;

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.localStorage.setItem(key, JSON.stringify(updatedValue));
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		log.safeErrorWithoutCustomerData(
			'rlabs.localStorage.error',
			`Unable to set rlabs graph configuration in localStorage with error: ${error.toString()}`,
		);
	}
	return undefined;
};
